import React from "react";

import { select } from "d3-selection";

import { ChartDataPoint } from "./ChartContext";
import styles from "./StandardBarChart.scss";

const xInitialPosition = -200;
const yInitialPosition = -200;

interface ChartTooltipProps {
  data: ChartDataPoint;
  showTooltip: boolean;
  svgRef: React.RefObject<SVGSVGElement>;
  tooltipContent: (args: { data: ChartDataPoint }) => React.ReactNode;
}

export function ChartTooltip({
  data,
  showTooltip,
  svgRef,
  tooltipContent,
}: ChartTooltipProps) {
  const foreignObjectWidth = 160;

  React.useEffect(() => {
    const svgElement = select(svgRef.current);
    const gElement = svgElement.select(".chart-tooltip");
    const tooltipContainer = svgElement.select(
      "." + styles["chart-tooltip-container"]
    );
    const tooltipContainerProps = {
      height: parseInt(tooltipContainer.style("height"), 10),
      paddingTop: parseInt(tooltipContainer.style("padding-top"), 10),
      paddingBottom: parseInt(tooltipContainer.style("padding-bottom"), 10),
    };

    const foreignObjectHeight = tooltipContainerProps.height;
    const rectHeight = tooltipContainerProps.height;

    if (showTooltip) {
      gElement.attr("opacity", "1");
    } else {
      gElement.attr("opacity", "0").attr("transform", "translate(-500, -500)");
    }

    svgElement
      .select("." + styles["chart-tooltip-box"])
      .attr("height", rectHeight);

    svgElement
      .select(".chart-tooltip-foreign-object")
      .attr("height", foreignObjectHeight);
  }, [data, showTooltip]);

  return (
    <>
      <g className="chart-tooltip" data-testid="chart-tooltip">
        <defs>
          <filter id="chart-tooltip-def">
            <feDropShadow
              className="chart-tooltip-filter-effect"
              dx={0}
              dy={0}
              stdDeviation={2}
            />
          </filter>
        </defs>
        <rect
          className={styles["chart-tooltip-box"]}
          x={xInitialPosition}
          y={yInitialPosition}
          width={foreignObjectWidth}
          height={86}
          filter="url(#chart-tooltip-def)"
        />
        <foreignObject
          className="chart-tooltip-foreign-object"
          x={xInitialPosition}
          y={yInitialPosition}
          height={30}
          width={foreignObjectWidth}
        >
          <div className={styles["chart-tooltip-container"]}>
            {tooltipContent({ data })}
          </div>
        </foreignObject>
      </g>
    </>
  );
}

// istanbul ignore next - requires an actual screen to test
export function tooltipCoords(
  svgRef: React.RefObject<SVGSVGElement>,
  [xPoint, yPoint]: [number, number]
): [number, number] {
  const xOffSet = 20;
  const svgElement = select(svgRef.current);
  const svgWidth = parseInt(svgElement.style("width"), 10);
  const svgX = parseInt(svgElement.style("x"), 10);

  const svgMiddleX = svgX + svgWidth / 2;
  const tooltipElem = select("." + styles["chart-tooltip-box"]);
  const tooltipHeight = parseInt(tooltipElem.attr("height"), 10);
  const tooltipWidth = parseInt(tooltipElem.attr("width"), 10);

  // After checking in different browsers we need to add an additional value equals to
  // the initial position given to the elements otherwise it doesn't match later
  // The initial position is to hide the initial rendering of tooltip component when page
  // is loaded.
  let tooltipX = xPoint - xInitialPosition + xOffSet;
  const tooltipY = yPoint - yInitialPosition - tooltipHeight;

  if (xPoint > svgMiddleX) {
    tooltipX = xPoint - xInitialPosition - xOffSet - tooltipWidth;
  }

  return [tooltipX, tooltipY];
}
